import type { Card, Suit } from '@campfire/cards';
import type { CrazyEightsEvent, CrazyEightsView } from '@campfire/game-crazy-eights';
import { cardFace, cardLabel, pile } from '../cards.ts';
import { h } from '../dom.ts';
import { name, others, type GameUi } from '../games.ts';

const SUIT_NAME: Record<Suit, string> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const SUIT_GLYPH: Record<Suit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_SEL = 'c8-eight';

export const crazyEightsUi: GameUi = {
  id: 'crazy-eights',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as CrazyEightsView;
    const n = v.handCounts[s] ?? 0;
    return `${n} card${n === 1 ? '' : 's'}${v.target !== null ? ` · ${v.scores[s] ?? 0} pts` : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as CrazyEightsView;
    const hand = v.hand ?? [];
    const eightFor = ctx.local.get<string>(SUIT_SEL);
    const play = (card: Card, suit?: Suit) => {
      ctx.local.set(SUIT_SEL, undefined);
      ctx.act({ type: 'play', card: card.id, ...(suit ? { suit } : {}) });
    };
    const ev = (e: CrazyEightsEvent | null): string => {
      if (!e) return `${name(ctx, v.turn)} starts. Match the suit or rank — 8s are wild.`;
      const who = name(ctx, 'seat' in e ? e.seat : e.winner);
      switch (e.kind) {
        case 'round':
          return `${who} won round ${e.round}${e.blocked ? ' (nobody could play — lowest hand)' : ''}: +${e.points} points.${v.result ? '' : ' New deal!'}`;
        case 'pass':
          return `${who} passed.`;
        case 'draw':
          return `${who} drew ${e.count === 1 ? 'a card' : `${e.count} cards`}${e.cards ? ` (${e.cards.map(cardLabel).join(' ')})` : ''}${e.playable ? '' : '.'}`;
        case 'play':
          return `${who} played ${cardLabel(e.card)}${e.suit ? ` — ${SUIT_NAME[e.suit]} now` : ''}.`;
      }
    };
    const pending = v.pending ? hand.find((c) => c.id === v.pending) : undefined;
    const canDraw = ctx.myTurn && !v.pending && v.playable.length === 0 && v.canDraw;
    const mustPass = ctx.myTurn && !v.pending && v.playable.length === 0 && !v.canDraw;
    return h(
      'div.c8-table',
      {},
      others(ctx, v.handCounts),
      h(
        'div.row.uno-center',
        {},
        h(
          `${canDraw ? 'button' : 'div'}.ocean`,
          { 'data-testid': 'c8-draw', onclick: canDraw ? () => ctx.act({ type: 'draw' }) : undefined },
          pile(v.stockCount),
          h('div.muted', {}, canDraw ? 'Tap to draw' : 'Stock'),
        ),
        h(
          'div.uno-top',
          {},
          cardFace(v.top, { testid: 'c8-top' }),
          h(`div.c8-suit${v.suit === 'H' || v.suit === 'D' ? '.red' : ''}`, { 'data-testid': 'c8-suit' }, v.suit ? `${SUIT_GLYPH[v.suit]} ${SUIT_NAME[v.suit]}` : 'Anything goes'),
        ),
      ),
      h('div.event', { 'data-testid': 'event' }, ev(v.last)),
      pending
        ? h(
            'div.hint',
            {},
            `You drew ${cardLabel(pending)} — play it`,
            v.drawRule === 'one' ? [' or ', h('button', { 'data-testid': 'c8-keep', onclick: () => ctx.act({ type: 'pass' }) }, 'Keep it')] : '.',
          )
        : mustPass
          ? h('div.hint', {}, 'Nothing fits and nothing left to draw. ', h('button', { 'data-testid': 'c8-pass', onclick: () => ctx.act({ type: 'pass' }) }, 'Pass'))
          : ctx.myTurn
            ? h('div.hint', { 'data-testid': 'hint' }, v.playable.length ? 'Tap a card to play it.' : 'Nothing fits — draw a card.')
            : null,
      eightFor && ctx.myTurn && hand.some((c) => c.id === eightFor)
        ? h(
            'div.row.actions',
            { 'data-testid': 'c8-suits' },
            h('span.muted', {}, 'Name a suit:'),
            (['S', 'H', 'D', 'C'] as const).map((s) =>
              h(`button.c8-suit-btn${s === 'H' || s === 'D' ? '.red' : ''}`, { 'data-testid': `c8-suit-${s}`, onclick: () => play(hand.find((c) => c.id === eightFor) as Card, s) }, `${SUIT_GLYPH[s]} ${SUIT_NAME[s]}`),
            ),
          )
        : null,
      v.hand
        ? h(
            'div.hand',
            { 'data-testid': 'hand' },
            hand.map((c) => {
              const ok = v.playable.includes(c.id);
              return cardFace(c, {
                selected: c.id === eightFor,
                onclick: ok ? () => (c.rank === '8' ? ctx.local.set(SUIT_SEL, c.id === eightFor ? undefined : c.id) : play(c)) : undefined,
              });
            }),
          )
        : null,
      h('div.muted', {}, `Round ${v.round}${v.target !== null ? ` · first to ${v.target}` : ''} · ${v.drawRule === 'one' ? 'draw one, then pass' : 'draw until you can play'}`),
    );
  },
};
